import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, User, Save, Loader2, MapPin, Calendar, Clock, Pencil, X, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";

const API = "/api";

const statusLabels = {
  pending: { text: "En attente", cls: "bg-yellow-500/20 text-yellow-400" },
  confirmed: { text: "Confirmee", cls: "bg-emerald-500/20 text-emerald-400" },
  in_progress: { text: "En cours", cls: "bg-blue-500/20 text-blue-400" },
};

export const ClientProfilePage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader, loginWithToken, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [editingBooking, setEditingBooking] = useState(null);
  const [bookingSaving, setBookingSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [meRes, bookingsRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { headers: getAuthHeader() }),
          axios.get(`${API}/bookings`, { headers: getAuthHeader() })
        ]);
        setProfile(meRes.data);
        const now = new Date();
        const modifiable = (bookingsRes.data || []).filter(b =>
          !["cancelled", "completed"].includes(b.status) &&
          new Date(b.pickup_datetime || b.pickup_date) > now
        );
        setBookings(modifiable);
      } catch (err) {
        console.error(err);
        toast.error("Erreur de chargement");
      }
      setLoading(false);
    };
    fetchData();
  }, [getAuthHeader]);

  const saveProfile = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/user/profile`, {
        first_name: profile.first_name,
        last_name: profile.last_name,
        email: profile.email,
        phone: profile.phone
      }, { headers: getAuthHeader() });
      toast.success(res.data.message);
      if (res.data.user) {
        loginWithToken(token, res.data.user);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const saveBookingModification = async () => {
    if (!editingBooking) return;
    setBookingSaving(true);
    try {
      const res = await axios.put(`${API}/bookings/${editingBooking.id}/modify`, {
        pickup_address: editingBooking.pickup_address,
        destination_address: editingBooking.destination_address
      }, { headers: getAuthHeader() });
      toast.success("Course mise a jour");
      setBookings(prev => prev.map(b => b.id === editingBooking.id ? res.data : b));
      setEditingBooking(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de modification");
    }
    setBookingSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="px-4 py-4 border-b border-zinc-800">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/client")} className="p-2 rounded-lg hover:bg-zinc-800 transition-colors" data-testid="back-client-btn">
            <ArrowLeft className="w-5 h-5 text-zinc-400" />
          </button>
          <User className="w-5 h-5 text-emerald-400" />
          <h1 className="text-lg font-bold text-white font-['Outfit']">Mon Profil</h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        {/* Personal Info */}
        {profile && (
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5" data-testid="profile-section">
            <h2 className="text-base font-bold text-white mb-4">Informations personnelles</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-zinc-400 text-xs block mb-1.5">Prenom</label>
                <Input
                  value={profile.first_name || ""}
                  onChange={e => setProfile({ ...profile, first_name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="profile-first-name"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1.5">Nom</label>
                <Input
                  value={profile.last_name || ""}
                  onChange={e => setProfile({ ...profile, last_name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="profile-last-name"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1.5">Email</label>
                <Input
                  type="email"
                  value={profile.email || ""}
                  onChange={e => setProfile({ ...profile, email: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="profile-email"
                />
              </div>
              <div>
                <label className="text-zinc-400 text-xs block mb-1.5">Telephone</label>
                <Input
                  value={profile.phone || ""}
                  onChange={e => setProfile({ ...profile, phone: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="profile-phone"
                />
              </div>
            </div>
            <Button
              onClick={saveProfile}
              disabled={saving}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
              data-testid="save-profile-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </section>
        )}

        {/* Modifiable Bookings */}
        <section data-testid="bookings-modify-section">
          <h2 className="text-base font-bold text-white mb-3">
            Courses a venir ({bookings.length})
          </h2>
          {bookings.length === 0 ? (
            <div className="text-center py-8 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              <Calendar className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">Aucune course a venir</p>
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.map(b => {
                const dt = b.pickup_datetime || b.pickup_date;
                const pickupDate = dt ? new Date(dt) : new Date();
                const st = statusLabels[b.status] || statusLabels.pending;
                const isEditing = editingBooking?.id === b.id;

                return (
                  <div key={b.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid={`booking-modify-${b.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="text-white text-sm font-medium">
                          {format(pickupDate, "EEE d MMM", { locale: fr })}
                        </span>
                        <Clock className="w-3.5 h-3.5 text-emerald-500 ml-1" />
                        <span className="text-white text-sm">{format(pickupDate, "HH:mm")}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.text}</span>
                        <span className="text-emerald-500 font-bold text-sm">{(b.price || b.total_price || 0).toFixed ? (b.price || b.total_price || 0).toFixed(2) : b.price || b.total_price}€</span>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="space-y-3">
                        <div>
                          <label className="text-zinc-400 text-xs block mb-1">Point de prise en charge</label>
                          <Input
                            value={editingBooking.pickup_address || ""}
                            onChange={e => setEditingBooking({ ...editingBooking, pickup_address: e.target.value })}
                            className="bg-zinc-800 border-zinc-700 text-white text-sm"
                            data-testid="edit-pickup-address"
                          />
                        </div>
                        <div>
                          <label className="text-zinc-400 text-xs block mb-1">Destination</label>
                          <Input
                            value={editingBooking.destination_address || ""}
                            onChange={e => setEditingBooking({ ...editingBooking, destination_address: e.target.value })}
                            className="bg-zinc-800 border-zinc-700 text-white text-sm"
                            data-testid="edit-destination-address"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={saveBookingModification}
                            disabled={bookingSaving}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-sm h-9"
                            data-testid="save-booking-btn"
                          >
                            {bookingSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Valider</>}
                          </Button>
                          <Button
                            onClick={() => setEditingBooking(null)}
                            variant="outline"
                            className="border-zinc-700 text-zinc-400 text-sm h-9"
                            data-testid="cancel-edit-btn"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <div className="flex flex-col items-center pt-0.5">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                          <div className="w-0.5 h-5 bg-zinc-700" />
                          <div className="w-2 h-2 bg-red-500 rounded-full" />
                        </div>
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="text-xs text-zinc-300 truncate">{b.pickup_address}</p>
                          <p className="text-xs text-zinc-300 truncate">{b.destination_address}</p>
                        </div>
                        <button
                          onClick={() => setEditingBooking({ ...b })}
                          className="p-2 rounded-lg hover:bg-zinc-800 transition-colors shrink-0"
                          data-testid={`edit-booking-${b.id}`}
                        >
                          <Pencil className="w-4 h-4 text-zinc-400" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};
